<template>
  <footer id="contact" class="contact-footer" :style="footerStyle" aria-labelledby="contact-title">
    <h2 id="contact-title" class="contact-footer__title">Contact</h2>

    <nav class="contact-footer__links" aria-label="Contact links">
      <RouterLink to="/user-agreement">Terms of Service</RouterLink>
      <RouterLink to="/privacy">Privacy Policy</RouterLink>
      <RouterLink to="/vip-service-agreement">VIP Service Agreement</RouterLink>
      <a class="contact-footer__support" href="mailto:creator@storeel.com" @click="handleMailClick">creator@storeel.com</a>
      <a class="contact-footer__support" href="mailto:support@storeel.com" @click="handleMailClick">support@storeel.com</a>
      <a class="contact-footer__support" href="mailto:bd@storeel.com" @click="handleMailClick">bd@storeel.com</a>
    </nav>

    <Transition name="email-toast">
      <div v-if="mailToast" class="email-toast" role="status" aria-live="polite">
        {{ mailToast }}
      </div>
    </Transition>
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { CSSProperties } from 'vue'

import { useMailto } from '../../composables/useMailto'

const props = defineProps<{
  scaledPx: (value: number) => string
}>()

const { mailToast, openMailto } = useMailto()

const footerStyle = computed<CSSProperties>(() => ({
  '--contact-max-width': props.scaledPx(1680),
  '--contact-padding-bottom': props.scaledPx(100),
  '--contact-title-font': props.scaledPx(60),
  '--contact-title-line': props.scaledPx(75),
  '--contact-link-gap': props.scaledPx(50),
  '--contact-link-font': props.scaledPx(20),
  '--contact-link-line': props.scaledPx(30),
  '--contact-support-font': props.scaledPx(22),
}))

const handleMailClick = (event: MouseEvent) => {
  event.preventDefault()
  const target = event.currentTarget
  if (!(target instanceof HTMLAnchorElement)) return

  void openMailto(target.href)
}
</script>

<style scoped>
.contact-footer {
  box-sizing: border-box;
  width: 100%;
  max-width: var(--contact-max-width);
  margin: 0 auto;
  padding-bottom: var(--contact-padding-bottom);
  background: #000;
  color: #fff;
  font-family: 'Alibaba PuHuiTi 3.0', AlibabaPuHuiTi_3_55_Regular, PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-synthesis: none;
  text-align: center;
  -webkit-font-smoothing: antialiased;
  text-rendering: geometricprecision;
}

.contact-footer__title {
  height: var(--contact-title-line);
  margin: 0;
  color: #fff;
  font-family: AlibabaPuHuiTi_3_85_Bold, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--contact-title-font);
  font-weight: 700;
  line-height: var(--contact-title-line);
  letter-spacing: 0;
  -webkit-font-smoothing: antialiased;
}

.contact-footer__links {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.contact-footer__links a {
  margin-top: var(--contact-link-gap);
  color: #fff;
  font-family: AlibabaPuHuiTi_3_65_Medium, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--contact-link-font);
  font-weight: 500;
  line-height: var(--contact-link-line);
  letter-spacing: 0;
  text-decoration: none;
  -webkit-font-smoothing: antialiased;
}

.contact-footer__links a:focus-visible {
  outline: var(--focus-outline-width) solid rgb(138 188 249 / 90%);
  outline-offset: var(--focus-outline-offset);
}

.contact-footer__links .contact-footer__support {
  background-image: linear-gradient(90deg, #ffff00 0%, #00ffff 100%);
  background-clip: text;
  color: transparent;
  font-size: var(--contact-support-font);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.email-toast {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 50;
  max-width: min(360px, calc(100vw - 48px));
  padding: 12px 16px;
  border: 1px solid rgb(255 255 255 / 18%);
  border-radius: 999px;
  background: rgb(17 17 17 / 92%);
  box-shadow: 0 14px 34px rgb(0 0 0 / 35%);
  color: #fff;
  font-family: AlibabaPuHuiTi_3_65_Medium, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  pointer-events: none;
}

.email-toast-enter-active,
.email-toast-leave-active {
  transition:
    opacity 160ms ease,
    transform 160ms ease;
}

.email-toast-enter-from,
.email-toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
  .email-toast-enter-active,
  .email-toast-leave-active {
    transition: none;
  }
}
</style>
